import { WithChildren } from '@/types/common'
import { createContext } from '@/ultils'
import { useCallback, useMemo, useReducer } from 'react'

const initialState: State = {
  displaySidebar: false,
  displayEditSidebar: false,
  displayModal: false,
  sidebarView: {
    type: '',
    id: '',
  },
  modalView: {
    type: '',
    id: '',
    onSuccess: undefined,
  },
}

export interface State {
  displaySidebar: boolean
  displayEditSidebar: boolean
  displayModal: boolean
  sidebarView: SIDEBAR_VIEW
  modalView: MODAL_VIEW
}

export type SIDEBAR_VIEW = {
  type: SIDEBAR_TYPES
  id?: string
  nameInterface?: string
  idInterface?: string
  idSite?: string
  idLocationView?: string
  parentId?: string
  parentName?: string
  name?: string
  des?: string
  role?: string
  status?: boolean
  email?: string
  organizationId?: string
  roleId?: number
  isAdminRootOrg?: boolean
}

export type MODAL_VIEW = {
  type: MODAL_TYPES
  id?: string
  interfaceId?: string
  subtitle?: string
  title?: string
  email?: string
  organizationId?: number
  onSuccess?: () => void
  typeUser?: string
  name?: string
  userId?: number
}

type Action =
  | {
      type: 'OPEN_SIDEBAR'
    }
  | {
      type: 'CLOSE_SIDEBAR'
    }
  | {
      type: 'SET_SIDEBAR_VIEW'
      sidebarView: SIDEBAR_VIEW
    }
  | {
      type: 'OPEN_MODAL'
    }
  | {
      type: 'CLOSE_MODAL'
    }
  | {
      type: 'SET_MODAL_VIEW'
      modalView: MODAL_VIEW
    }

export type SIDEBAR_TYPES =
  | 'INTERFACE_VIEW'
  | 'SITE_VIEW'
  | 'FLOOR_VIEW'
  | 'WEATHER_LINK_VIEW'
  | 'LOCATION_VIEW'
  | 'ADD_ORGANIZATION_VIEW'
  | 'EDIT_ORGANIZATION_VIEW'
  | 'ADMINISTRATOR_VIEW'
  | 'ORGANIZATION_ADD_VIEW'
  | 'MANAGE_USER_VIEW'
  | 'ORGANIZATION_MANAGE_VIEW'
  | 'ORGANIZATION_EXISTING_USER_VIEW'
  | 'ORGANIZATION_EXISTING_USER_VIEW_FOR_SUPERUSER'
  | 'ORGANIZATION_EXISTING_USER_VIEW_V2'
  | 'ORGANIZATION_USER_VIEW'
  | ''
export type MODAL_TYPES =
  | 'ADD_SITE_VIEW'
  | 'EDIT_SITE_VIEW'
  | 'DELETE_SITE_MODAL'
  | 'EDIT_WEATHER_MODAL'
  | 'DELETE_ORGANIZATION_MODAL'
  | 'DELETE_USER_MODAL'
  | 'DELETE_LOCATION_MODAL'
  | 'CHANGE_PASSWORD_VIEW'
  | 'DELETE_ADMIN_MODAL'
  | 'RESET_INTERFACE_MODAL'
  | 'REMOVE_USER_EXISTING_ORGANIZATION'
  | 'DELETE_MANAGE_USER_MODAL'
  | 'ADD_USER_EXISTING_ORGANIZATION'
  | 'REMOVE_USER_EXISTING_ORGANIZATION_V2'
  | 'REMOVE_USER_INTERFACE_ORG_MODAL'
  | 'REMOVE_SENSOR_ORGANIZATION_MODAL'
  | 'INTERFACE_BACKUP_MODAL'
  | ''

interface AdminContextValues extends State {
  openSidebar: () => void
  closeSidebar: () => void
  toggleSidebar: () => void
  setSidebarView: (sidebarView: SIDEBAR_VIEW) => void
  openModal: () => void
  closeModal: () => void
  setModalView: (modalView: MODAL_VIEW) => void
}

function uiReducer(state: State, action: Action) {
  switch (action.type) {
    case 'OPEN_SIDEBAR': {
      return {
        ...state,
        displaySidebar: true,
      }
    }
    case 'CLOSE_SIDEBAR': {
      return {
        ...state,
        displaySidebar: false,
      }
    }
    case 'SET_SIDEBAR_VIEW': {
      return {
        ...state,
        sidebarView: {
          ...action.sidebarView,
        },
      }
    }

    case 'OPEN_MODAL': {
      return {
        ...state,
        displayModal: true,
      }
    }
    case 'CLOSE_MODAL': {
      return {
        ...state,
        displayModal: false,
      }
    }
    case 'SET_MODAL_VIEW': {
      return {
        ...state,
        modalView: action.modalView,
      }
    }
  }
}

const [Provider, useAdminUI] = createContext<AdminContextValues>({
  name: 'adminUI',
})

const AdminUIProvider = ({ children }: WithChildren) => {
  const [state, dispatch] = useReducer(uiReducer, initialState)

  const openSidebar = useCallback(
    () => dispatch({ type: 'OPEN_SIDEBAR' }),
    [dispatch],
  )

  const closeSidebar = useCallback(
    () => dispatch({ type: 'CLOSE_SIDEBAR' }),
    [dispatch],
  )

  const toggleSidebar = useCallback(
    () =>
      state.displaySidebar
        ? dispatch({ type: 'CLOSE_SIDEBAR' })
        : dispatch({ type: 'OPEN_SIDEBAR' }),
    [dispatch, state.displaySidebar],
  )

  const setSidebarView = useCallback(
    (sidebarView: SIDEBAR_VIEW) =>
      dispatch({ type: 'SET_SIDEBAR_VIEW', sidebarView }),
    [dispatch],
  )

  const openModal = useCallback(
    () => dispatch({ type: 'OPEN_MODAL' }),
    [dispatch],
  )

  const closeModal = useCallback(
    () => dispatch({ type: 'CLOSE_MODAL' }),
    [dispatch],
  )

  const setModalView = useCallback(
    (modalView: MODAL_VIEW) => dispatch({ type: 'SET_MODAL_VIEW', modalView }),
    [dispatch],
  )

  const value = useMemo(
    () => ({
      ...state,
      openSidebar,
      closeSidebar,
      toggleSidebar,
      setSidebarView,
      openModal,
      closeModal,
      setModalView,
    }),
    // eslint-disable-next-line
    [state],
  )

  return <Provider value={value}>{children}</Provider>
}

export { AdminUIProvider, useAdminUI }
